// Sma200Widget/Sma200Engine.js
// Engine Pattern (rk=f03ae8b6) — extends EngineCleanupBase, singleton export.
// bootup+fetch() called from PageContent at app boot (same as FearGreedEngine), RMQ
// pushes daily per-asset updates from there on via onSma200Received (wired in
// PageContentSRHandlers once that side exists).
import EngineCleanupBase from '../../../services/engines/EngineCleanupBase';
import ComponentNotifier from '../../../services/wss/ComponentNotifier';
import sma200Cache from './Sma200Cache';
import sma200Fetcher from './Sma200Fetcher';

const _name = 'Sma200Engine';

class Sma200Engine extends EngineCleanupBase {
    bootup = () => {
        this.initialize(_name);
    };

    /**
     * Initial fetch for ALL assets (sid=0, window=200) — called once from PageContent
     * alongside the other engines' fetch/fetchAll calls.
     * @returns {Promise<void>}
     */
    fetch = async () => {
        try {
            const limit = 10;
            const items = await sma200Fetcher.fetchAll(limit);
            if (!items.length) return;

            sma200Cache.addOrUpdate(items);
            this._notify(null);
        } catch (error) {
            this.logError('fetch', error.message);
        }
    };

    /**
     * RMQ push handler. CacheBase2.addOrUpdate() requires an array (it calls
     * data.forEach) — wraps a bare single object into one.
     * @param {object|object[]} data {rk,root,sid,sum,value,price,ems}
     */
    onSma200Received = (data) => {
        try {
            const items = Array.isArray(data) ? data : [data];
            sma200Cache.addOrUpdate(items);
            this._notify(items[0]?.sid ?? null);
        } catch (error) {
            this.logError('onSma200Received', error.message);
        }
    };

    /**
     * Latest SMA200 slot for one asset.
     * @param {number} sid
     * @returns {{value:number,price:number,ems:number}|null}
     */
    getState = (sid) => {
        const items = sma200Cache.getAll().filter((i) => i.sid === sid);
        if (!items.length) return null;

        return items.reduce((latest, i) => (i.ems > latest.ems ? i : latest));
    };

    /**
     * @param {number|null} sid - which asset changed, or null for a bulk refresh
     */
    _notify = (sid) => {
        ComponentNotifier.notify(_name, 'dataChanged', { sid });
    };
}

const sma200Engine = new Sma200Engine();
export default sma200Engine;
